import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { MultiplayerOrchestrator } from "../../../runtime/orchestrator/MultiplayerOrchestrator";
import { MultiplayerViewState } from "../../../runtime/orchestrator/MultiplayerViewState";
import { useMultiplayerOrchestrator } from "../../../runtime/orchestrator/useMultiplayerOrchestrator";

export interface OrchestratorContextValue {
  orchestrator: MultiplayerOrchestrator | null;
  setOrchestrator: (next: MultiplayerOrchestrator | null) => void;
  viewState: MultiplayerViewState;
}

const OrchestratorContext = createContext<OrchestratorContextValue | null>(
  null
);

export const OrchestratorProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [orchestrator, setOrchestratorState] =
    useState<MultiplayerOrchestrator | null>(null);
  const viewState = useMultiplayerOrchestrator(orchestrator);

  const setOrchestrator = useCallback(
    (next: MultiplayerOrchestrator | null) => {
      setOrchestratorState((current) => {
        if (current && current !== next) {
          current.dispose();
        }
        return next;
      });
    },
    []
  );

  useEffect(() => {
    return () => {
      setOrchestratorState((current) => {
        if (current) {
          current.dispose();
        }
        return null;
      });
    };
  }, []);

  return (
    <OrchestratorContext.Provider
      value={{ orchestrator, setOrchestrator, viewState }}
    >
      {children}
    </OrchestratorContext.Provider>
  );
};

export function useOrchestrator(): OrchestratorContextValue {
  const context = useContext(OrchestratorContext);
  if (!context) {
    throw new Error(
      "useOrchestrator must be used within an OrchestratorProvider"
    );
  }
  return context;
}

